import { AdScheduleStats } from './../../../interfaces/adschedule';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import dayjs from 'dayjs';

import { SapiService } from '../../../services/sapi.service';
import { DateRange } from '../../../interfaces/dateRange';

export interface StatsBlock {
	procent: boolean;
	title: string;
	name: string;
	helpText: string;
	metric?: number;
	metric_compare?: number;
	class?: string;
	trend?: string;
	arrow?: string;
	warningText?: string;
}

@Component({
	selector: 'bb-adstats-blocks',
	templateUrl: './bb-adstats-blocks.component.html',
	styleUrls: ['./bb-adstats-blocks.component.scss']
})

export class BbAdstatsBlocksComponent implements OnInit, OnChanges {

	@Input() id: string;
	@Input() adunitCode: string;
	@Input() lineitemCode: string;
	@Input() statsDate: DateRange;
	@Input() adScheduleStats: AdScheduleStats;
	@Input() hideInView: boolean;

	statsBlocks: StatsBlock[] = [];
	stats: any[];

	newDataFromDate: Date;

	constructor(private sapiService: SapiService, private translate: TranslateService) {
	}

	ngOnInit() {
		const statsBlocks = [
			{
				name: this.adScheduleStats ? 'inventory' : 'inits',
				procent: false,
				title: this.translate.instant('INVENTORY'),
				helpText: this.translate.instant('HELP.ADSTATS_INVENTORY')
			},
			{
				name: 'inview',
				procent: false,
				title: this.translate.instant('IN_VIEW'),
				helpText: this.translate.instant('HELP.ADSTATS_IN_VIEW')
			},
			{
				name: 'views',
				procent: false,
				title: this.translate.instant('IMPRESSIONS'),
				helpText: this.translate.instant('HELP.ADSTATS_IMPRESSIONS')
			},
			{
				name: 'fillrate',
				procent: true,
				title: this.translate.instant('FILL_RATE'),
				helpText: this.translate.instant('HELP.ADSTATS_FILL_RATE')
			},
			{
				name: 'clickrate',
				procent: true,
				title: this.translate.instant('CTR'),
				helpText: this.translate.instant('HELP.ADSTATS_CTR')
			},
			{
				name: 'placementRate',
				procent: true,
				title: this.translate.instant('PLACEMENT_RATE'),
				helpText: this.translate.instant('HELP.ADSTATS_PLACEMENT_RATE')
			}
		];

		if (this.hideInView) {
			statsBlocks.splice(1,1);
		}

		this.statsBlocks = statsBlocks;
		this.newDataFromDate = new Date('2018-07-16T14:00:00.000Z');
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges?.statsDate?.currentValue) {
			if (simpleChanges.statsDate.previousValue === undefined || simpleChanges.statsDate.previousValue !== simpleChanges.statsDate.currentValue) {
				if ((this.lineitemCode === undefined || this.lineitemCode !== null) && (this.adunitCode === undefined || this.adunitCode !== null)) {
					this.datesChanged(simpleChanges.statsDate.currentValue);
				}
			}
		}

		if (simpleChanges?.adScheduleStats?.currentValue) {
			this.updateStatsBlocks(simpleChanges?.adScheduleStats?.currentValue);
		}

		if (this.statsDate && simpleChanges?.lineitemCode?.currentValue) {
			if (this.adunitCode === undefined || this.adunitCode) {
				this.datesChanged(this.statsDate);
			}
		}

		if (this.statsDate && simpleChanges?.adunitCode?.currentValue) {
			if (this.lineitemCode === undefined || this.lineitemCode) {
				this.datesChanged(this.statsDate);
			}
		}
	}

	updateStatsBlocks(stats) {
		let statsWarning = false;
		if (this.statsDate && this.statsDate.startDate) {
			const earliestDate = new Date(this.statsDate.startDate.getTime() - (this.statsDate.endDate.getTime() - this.statsDate.endDate.getTime()));
			if (earliestDate < this.newDataFromDate) {
				statsWarning = true;
			}
		}
		this.statsBlocks.forEach((statsBlock: StatsBlock) => {
			const name = statsBlock.name;
			statsBlock.metric = stats[name] || 0;
			statsBlock.metric_compare = stats[name + '_compare'] || 0;
			statsBlock.class = 'gray';

			if (statsWarning) {
				if (statsBlock.name === 'inview') {
					statsBlock.warningText = this.translate.instant('HELP.INVIEW_MEASURE_DATE_WARNING', { date: Date.now() });
				} else if (statsBlock.name === 'placementrate') {
					statsBlock.warningText = this.translate.instant('HELP.PLACEMENT_RATE_MEASURE_DATE_WARNING', { date: Date.now() });
				}
			}

			if (statsBlock.metric_compare === 0) {
				statsBlock.trend = this.translate.instant('NOT_APPLICABLE');
			} else if (statsBlock.metric_compare > 0) {
				statsBlock.trend = (((100 / statsBlock.metric_compare) * statsBlock.metric * 1.00) - 100).toFixed(2);
			}

			if (Number(statsBlock.trend) > 0) {
				statsBlock.class = 'green';
				statsBlock.arrow = 'fa-arrow-circle-up';
			} else if (Number(statsBlock.trend) < 0) {
				statsBlock.class = 'red';
				statsBlock.arrow = 'fa-arrow-circle-down';
				statsBlock.trend = Math.abs(Number(statsBlock.trend)).toFixed(2);
			} else {
				statsBlock.trend = this.translate.instant('NOT_APPLICABLE');
			}
		});
	}

	datesChanged(dateRange: DateRange) {
		this.getStats(this.statsDate).subscribe((stats: any[]) => {
			this.updateStatsBlocks(stats);
		});

	}

	private getStats(statsDate?: DateRange): Observable<any[]> {
		let params: HttpParams = null;

		if (statsDate) {
			if (dayjs(0, 'HH').diff(statsDate.startDate, 'days') === 0) {
				params = new HttpParams().set('start', dayjs(statsDate.startDate).toISOString())
					.set('end', dayjs(statsDate.endDate).toISOString())
					.set('compare_start', dayjs(statsDate.startDate).subtract(1, 'days').toISOString())
					.set('compare_end', dayjs(statsDate.endDate).subtract(1, 'days').toISOString());
			} else {
				params = new HttpParams().set('start', dayjs(statsDate.startDate).toISOString())
					.set('end', dayjs(statsDate.endDate).toISOString());
			}
		}

		if (this.lineitemCode) {
			params = params.append('lineitem', this.lineitemCode);
		}

		if (this.adunitCode) {
			params = params.append('adunit', this.adunitCode);
		}

		return this.sapiService.getUrl('/adstats', params);

	}
}
